import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen, Compass, Map, MessageCircle } from "lucide-react";
import { useLanguage } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];

const COPY = {
  it: {
    kicker: "Welcome Guide & Concierge digitale",
    title: "La tua casa. Tutto il territorio. Un'unica guida.",
    sub: "Ogni ospite OGStay riceve l'accesso alla Welcome Guide digitale: la casa, il territorio e le esperienze in un unico strumento, sempre a portata di mano.",
    features: [
      { icon: BookOpen, t: "Guida della casa", d: "Tutto ciò che serve sapere sulla tua struttura, sempre disponibile." },
      { icon: Map, t: "Il territorio", d: "Spiagge, itinerari e luoghi autentici dell'Ogliastra, selezionati da chi ci vive." },
      { icon: Compass, t: "Esperienze", d: "Attività e partner locali per vivere il territorio da protagonista." },
      { icon: MessageCircle, t: "Concierge digitale", d: "Risposte immediate sul soggiorno e sull'Ogliastra, direttamente dal sito." },
    ],
    cta: "Apri la Welcome Guide",
    note: "welcome.ogstay.com",
    imgAlt: "La Welcome Guide OGStay su smartphone: home della casa, servizi extra e indicazioni per arrivare",
  },
  en: {
    kicker: "Welcome Guide & Digital concierge",
    title: "Your home. The whole territory. One guide.",
    sub: "Every OGStay guest gets access to the digital Welcome Guide: the home, the territory and the experiences in a single tool, always at hand.",
    features: [
      { icon: BookOpen, t: "Home guide", d: "Everything you need to know about your property, always available." },
      { icon: Map, t: "The territory", d: "Beaches, itineraries and authentic places in Ogliastra, selected by locals." },
      { icon: Compass, t: "Experiences", d: "Activities and local partners to experience the territory first-hand." },
      { icon: MessageCircle, t: "Digital concierge", d: "Instant answers about your stay and Ogliastra, right from the site." },
    ],
    cta: "Open the Welcome Guide",
    note: "welcome.ogstay.com",
    imgAlt: "The OGStay Welcome Guide on smartphones: home page, extra services and directions",
  },
};

export default function WelcomeGuide() {
  const { lang } = useLanguage();
  const c = COPY[lang];
  return (
    <section id="welcome-guide" data-testid="welcome-guide-section" className="bg-sand py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="kicker text-navy">
            <span className="h-px w-10 bg-sky" />
            {c.kicker}
          </p>
          <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-navy sm:text-5xl">{c.title}</h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-mute sm:text-lg">{c.sub}</p>
          <a
            data-testid="welcome-guide-cta"
            href="https://welcome.ogstay.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-dark mt-9"
          >
            {c.cta}
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-mute">{c.note}</p>
        </motion.div>
        <motion.div
          data-testid="welcome-guide-mockup"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE, delay: 0.1 }}
          className="relative"
        >
          <div className="pointer-events-none absolute inset-x-6 bottom-6 h-24 rounded-full bg-navy/15 blur-3xl" />
          <img
            src="/welcome-guide-mockup.webp"
            alt={c.imgAlt}
            width="1448"
            height="1086"
            loading="lazy"
            className="relative w-full drop-shadow-[0_40px_60px_rgba(15,43,58,0.22)]"
          />
        </motion.div>
      </div>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {c.features.map((f, i) => (
            <motion.div
              key={f.t}
              data-testid={`welcome-feature-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }}
              className="rounded-[1.5rem] bg-white p-7 ring-1 ring-navy/10 transition-shadow duration-500 hover:shadow-[0_24px_50px_-24px_rgba(15,43,58,0.3)]"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-sky">
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-display text-lg font-semibold text-navy">{f.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-mute">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
